import Image from 'next/image';

import {
  Box,
  CircularProgress,
  CircularProgressLabel,
  Flex,
  Heading,
  List,
  ListIcon,
  ListItem,
  Text,
} from '@chakra-ui/react';
import { CheckedIcon } from '@udacity/chakra-uds-icons';

import { Card } from '~/components/card';
import { ContentContainer } from '~/components/container';
import { HeadingWithEmphasis } from '~/components/heading-with-emphasis';
import { SanityImage } from '~/components/sanity-image';
import { SanityImageType } from '~/features/page-template/models/image';

export type SuccessStoriesSectionProps = {
  title: string;
  description: string;
  stats: Array<{
    _id: string;
    title: string;
    text: string;
    value: number;
  }>;
  people: Array<{
    _id: string;
    title: string;
    name: string;
    image: SanityImageType;
    bioOverviewList: string[];
    company: {
      _id: string;
      name: string;
      logo: SanityImageType;
    };
  }>;
};

export function SuccessStoriesSection({ title, description, stats, people }: SuccessStoriesSectionProps) {
  return (
    <Box
      as='section'
      backgroundColor='blue.800'
      color='white'
      overflow='hidden'
      position='relative'
      padding={{
        base: '40px 0px',
        md: '80px 0px',
      }}
    >
      <Box
        left='calc((100vw - 70rem)/2)'
        position='absolute'
        top='-20px'
        transform='translateX(-524px)'
        display={{
          base: 'none',
          md: 'block',
        }}
      >
        <Box height='600px' position='relative' width='2188px'>
          <Image
            alt=''
            sizes='2188px'
            src='/images/backgrounds/background-lines-blue-purple.svg'
            style={{
              objectFit: 'cover',
              objectPosition: 'top',
            }}
            fill
          />
        </Box>
      </Box>
      <ContentContainer contentMaxWidth='content-max-width-legacy' position='relative'>
        <Flex
          gap='40px'
          alignItems={{
            md: 'flex-start',
          }}
          flexDir={{
            base: 'column',
            md: 'row',
          }}
          marginBottom={{
            base: '40px',
            md: '48px',
          }}
        >
          <Box
            flex={{
              md: '0 2 528px',
            }}
          >
            <HeadingWithEmphasis
              text={title}
              emphasisProps={{
                fontWeight: '500',
              }}
              headingProps={{
                size: 'marketing-5xl',
                as: 'h2',
                marginBottom: '24px',
              }}
            />
            <Text>{description}</Text>
          </Box>
          <Flex
            gap='16px'
            justifyContent='space-between'
            flex={{
              md: '1 1 549px',
            }}
            flexDir={{
              base: 'column',
              md: 'row',
            }}
          >
            {stats.map((stat) => (
              <Card
                key={stat._id}
                alignItems='center'
                borderRadius='4px 16px 4px 4px'
                boxShadow='5px 5px 30px 5px rgba(0, 0, 0, 0.10)'
                color='black'
                display='flex'
                flexDir='column'
                justifyContent='center'
                padding='40px 24px'
                flex={{
                  md: '0 1 257px',
                }}
              >
                <CircularProgress
                  aria-label={`${stat.title}, ${stat.text}`}
                  color='blue.500'
                  mb={6}
                  size='128px'
                  value={stat.value}
                >
                  <CircularProgressLabel fontFamily='heading'>{stat.title}</CircularProgressLabel>
                </CircularProgress>
                <Text textAlign='center'>{stat.text}</Text>
              </Card>
            ))}
          </Flex>
        </Flex>
        <Box>
          <Heading as='h3' marginBottom='16px' size='marketing-3xl'>
            Student Success Stories
          </Heading>
          <Flex
            gap='32px'
            flexDir={{
              base: 'column',
              md: 'row',
            }}
          >
            {people.map((person) => (
              <Card
                key={person._id}
                as='article'
                backgroundColor='blue.700'
                border='none'
                flex='0 1 256px'
                padding='24px 16px'
                sx={{
                  '> div + ul': {
                    marginTop: '64px',
                    position: 'relative',
                  },
                  '> div + ul::before': {
                    content: '""',
                    position: 'absolute',
                    top: '-32px',
                    left: '0px',
                    right: '0px',
                    borderTop: '0.5px solid',
                    borderColor: 'accent.sky.500',
                  },
                }}
              >
                <Box>
                  <Box
                    borderRadius='50%'
                    height='40px'
                    marginBottom='8px'
                    overflow='hidden'
                    position='relative'
                    width='40px'
                  >
                    <SanityImage alt='' image={person.image} sizes='80px' fill />
                  </Box>
                  <Box marginBottom='16px'>
                    <Heading as='p' fontSize='0.875rem' fontWeight='500'>
                      {person.name}
                    </Heading>
                    <Text fontSize='0.875rem'>{person.title}</Text>
                  </Box>
                  <SanityImage alt={person.company.name} image={person.company.logo} sizes='200px' />
                </Box>
                <List display='flex' flexDir='column' gap='16px' paddingStart='40px'>
                  {person.bioOverviewList.map((bio) => (
                    <ListItem key={bio} position='relative'>
                      <ListIcon
                        as={CheckedIcon}
                        color='accent.lime'
                        height='32px'
                        left='-40px'
                        position='absolute'
                        width='32px'
                      />
                      {bio}
                    </ListItem>
                  ))}
                </List>
              </Card>
            ))}
          </Flex>
        </Box>
      </ContentContainer>
    </Box>
  );
}
